import React, { type ReactNode, useMemo } from "react"

import { type Role } from "../../containers/LoginPage/types/auth.types.ts"
import type {
  PermissionFeatures,
  PermissionPages,
  UserPermissions
} from "../../containers/SettingsPermissions/types/permissions.types.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { NotAllowed } from "./NotAllowed.tsx"

type PermissionCategories = keyof PermissionFeatures

// category.section.key, e.g. "projects.projects.project_create"
type PermissionPath = {
  [Cat in PermissionCategories]: {
    [Sec in keyof PermissionFeatures[Cat]]: {
      [
        Key in keyof PermissionFeatures[Cat][Sec]
      ]: `${Cat & string}.${Sec & string}.${Key & string}`
    }[keyof PermissionFeatures[Cat][Sec]]
  }[keyof PermissionFeatures[Cat]]
}[PermissionCategories]

interface PermissionGateProps {
  children: ReactNode
  /** Pages that should be in permissions.pages */
  pages?: PermissionPages[]
  /** Paths to permission (category.section.key) */
  paths?: PermissionPath[]
  /** "every" — all paths must be true, "some" — at least one */
  mode?: "every" | "some"
  /** Custom check. Wrap in useCallback to avoid unnecessary recalculations */
  check?: (permissions: UserPermissions, role: Role) => boolean
  fallback?: ReactNode
  description?: string
  hideOnDenied?: boolean
}

const EMPTY_PATHS: PermissionPath[] = []

// The untyped core: any `category.section.key` dot-path against the raw
// `codes` object, with no assumption about which paths actually exist. This
// is what a caller outside PermissionGate's own strict `PermissionPath`
// union (e.g. the Product Tour's `requiredPage`, a plain runtime string)
// should use — sharing this one traversal, rather than re-implementing it,
// is what keeps every permission gate in the app agreeing on one semantics
// for "missing category/section/key" (all `false`, never a throw).
export const getPermissionValueAtPath = (
  codes: UserPermissions["codes"] | unknown,
  path: string | undefined
): boolean => {
  const [category, section, key] = path?.split(".") ?? []
  const record = codes as
    | Record<string, Record<string, Record<string, unknown>>>
    | null
    | undefined
  return !!record?.[category]?.[section]?.[key]
}

const getPermissionByPath = (
  permissions: UserPermissions,
  path: PermissionPath
): boolean => getPermissionValueAtPath(permissions.codes, path)

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  pages,
  paths,
  mode = "every",
  check,
  fallback,
  description,
  hideOnDenied = false
}) => {
  const permissions = useAppSelector((state) => state.auth.permissions)
  const role = useAppSelector((state) => state.auth.userInfo?.role)

  const resolvedPaths = paths ?? EMPTY_PATHS

  const isAllowed = useMemo(() => {
    if (!permissions) return false

    const hasPageAccess =
      !pages?.length || pages.every((p) => permissions.pages?.includes(p))

    const hasPathsAccess =
      resolvedPaths.length === 0
        ? true
        : mode === "every"
          ? resolvedPaths.every((path) =>
              getPermissionByPath(permissions, path)
            )
          : resolvedPaths.some((path) => getPermissionByPath(permissions, path))

    const passesCheck =
      !check || (role !== undefined && check(permissions, role))

    return hasPageAccess && hasPathsAccess && passesCheck
  }, [permissions, role, pages, resolvedPaths, mode, check])

  if (isAllowed) return <>{children}</>
  if (hideOnDenied) return null

  return <NotAllowed fallback={fallback} description={description} />
}

export type { PermissionPath, PermissionGateProps }
